import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { CONTRACTS, ABIS, ILK, ILK_WPLS, RPC_URLS } from '../utils/contracts';

// ---------------------------------------------------------------------------
// The deep scan behind the System tab: every parameter and live figure in the
// deployment, read raw from the chain. Public view calls only; refreshes on a
// 30s cycle. This is the page for people who want to see how the machine is
// configured, not be told what to think about it.
// ---------------------------------------------------------------------------

const RAY = 10n ** 27n;

export const useSystemStatus = () => {
  const [data, setData] = useState({ isLoading: true, error: null });

  const fetchAll = useCallback(async () => {
    try {
      const provider = new ethers.FallbackProvider(
        RPC_URLS.map((url) => new ethers.JsonRpcProvider(url))
      );
      const vat = new ethers.Contract(CONTRACTS.VAT, ABIS.VAT, provider);
      const spotter = new ethers.Contract(CONTRACTS.SPOTTER, ABIS.SPOTTER, provider);
      const jug = new ethers.Contract(CONTRACTS.JUG, ABIS.JUG, provider);
      const median = new ethers.Contract(CONTRACTS.MEDIANIZER, ABIS.MEDIANIZER, provider);
      const vow = new ethers.Contract(CONTRACTS.VOW, ABIS.VOW_AUCTION, provider);
      const dog = new ethers.Contract(CONTRACTS.DOG, ABIS.DOG, provider);
      const flap = new ethers.Contract(CONTRACTS.FLAPPER, ABIS.FLAPPER, provider);
      const flop = new ethers.Contract(CONTRACTS.FLOPPER, ABIS.FLOPPER, provider);
      const calc = new ethers.Contract(CONTRACTS.CALC, ABIS.CALC, provider);
      const tkr = new ethers.Contract(CONTRACTS.TKR, ABIS.TKR, provider);
      const jdai = new ethers.Contract(CONTRACTS.JDAI, ABIS.JDAI, provider);
      const wpls = new ethers.Contract(CONTRACTS.PLS, ABIS.PLS, provider);

      const [
        Line, debt, vice, vatLive,
        ilkPls, ilkWpls, par, spotPls, spotWpls,
        jugBase, jugPls, jugWpls,
        medianAge, medianBar,
        vowDai, vowSin, Sin, Ash, bump, sump, dump, hump, wait,
        Hole, Dirt, dogPls, dogWpls,
        jdaiSupply, tkrSupply, tkrAuthority, tkrOwner,
        plsLocked, wplsLocked,
        calcCut, calcStep,
        flapBeg, flapTtl, flapTau, flapKicks, flapLive,
        flopBeg, flopPad, flopTtl, flopTau, flopKicks, flopLive
      ] = await Promise.all([
        vat.Line(), vat.debt(), vat.vice(), vat.live(),
        vat.ilks(ILK), vat.ilks(ILK_WPLS), spotter.par(), spotter.ilks(ILK), spotter.ilks(ILK_WPLS),
        jug.base(), jug.ilks(ILK), jug.ilks(ILK_WPLS),
        median.age(), median.bar(),
        vat.dai(CONTRACTS.VOW), vat.sin(CONTRACTS.VOW), vow.Sin(), vow.Ash(),
        vow.bump(), vow.sump(), vow.dump(), vow.hump(), vow.wait(),
        dog.Hole(), dog.Dirt(), dog.ilks(ILK), dog.ilks(ILK_WPLS),
        jdai.totalSupply(), tkr.totalSupply(), tkr.authority(), tkr.owner(),
        provider.getBalance(CONTRACTS.ETHJOIN), wpls.balanceOf(CONTRACTS.GEMJOIN_WPLS_A),
        calc.cut(), calc.step(),
        flap.beg(), flap.ttl(), flap.tau(), flap.kicks(), flap.live(),
        flop.beg(), flop.pad(), flop.ttl(), flop.tau(), flop.kicks(), flop.live()
      ]);

      // per-clipper params
      const clips = {};
      for (const [name, addr] of [['PLS-A', CONTRACTS.CLIPPER_PLS_A], ['WPLS-A', CONTRACTS.CLIPPER_WPLS_A]]) {
        const clip = new ethers.Contract(addr, ABIS.CLIPPER, provider);
        const [buf, tail, cusp, chip, tip, chost, kicks, count] = await Promise.all([
          clip.buf(), clip.tail(), clip.cusp(), clip.chip(), clip.tip(), clip.chost(), clip.kicks(), clip.count()
        ]);
        clips[name] = { buf, tail, cusp, chip, tip, chost, kicks, count };
      }

      // derived oracle prices (spot is the risk-adjusted price: pip/par/mat baked in)
      const oraclePls = (ilkPls.spot * spotPls.mat) / RAY; // ray: JDAI per PLS at par terms

      setData({
        isLoading: false, error: null, at: Date.now(),
        vat: { Line, debt, vice, live: vatLive },
        // ethers Result objects don't spread their NAMED fields — copy explicitly
        ilks: {
          'PLS-A': { Art: ilkPls.Art, rate: ilkPls.rate, spot: ilkPls.spot, line: ilkPls.line, dust: ilkPls.dust,
            mat: spotPls.mat, pip: spotPls.pip, duty: jugPls.duty, rho: jugPls.rho },
          'WPLS-A': { Art: ilkWpls.Art, rate: ilkWpls.rate, spot: ilkWpls.spot, line: ilkWpls.line, dust: ilkWpls.dust,
            mat: spotWpls.mat, pip: spotWpls.pip, duty: jugWpls.duty, rho: jugWpls.rho }
        },
        oracle: { par, age: Number(medianAge), bar: medianBar, oraclePls },
        jug: { base: jugBase },
        vow: { dai: vowDai, sin: vowSin, Sin, Ash, bump, sump, dump, hump, wait },
        dog: { Hole, Dirt, ilks: { 'PLS-A': dogPls, 'WPLS-A': dogWpls } },
        clips,
        calc: { cut: calcCut, step: calcStep },
        flap: { beg: flapBeg, ttl: flapTtl, tau: flapTau, kicks: flapKicks, live: flapLive },
        flop: { beg: flopBeg, pad: flopPad, ttl: flopTtl, tau: flopTau, kicks: flopKicks, live: flopLive },
        tokens: { jdaiSupply, tkrSupply, tkrAuthority, tkrOwner },
        locked: { pls: plsLocked, wpls: wplsLocked }
      });
    } catch (e) {
      setData((d) => ({ ...d, isLoading: false, error: e?.shortMessage || e?.message || 'read failed' }));
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll]);

  return data;
};
